import _defineProperty from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback, useState } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { v4 } from 'uuid';
import { useTranslation } from '@yoweb/next-i18next';
import { getFileTypeFromMime, isImageMimeType } from '@poplar/utils/file';
import { MAX_FILES, MAX_TOTAL_FILE_SIZE, MAX_FILE_SIZE } from '@yoweb/utils/file';

var sumArr = function sumArr(arr) {
  return arr.reduce(function (acc, curr) {
    return acc + curr;
  }, 0);
};

export var acceptedMimeTypes = ['image/heic', 'application/pdf', 'image/jpeg', 'image/png', 'image/gif'];

var compareFiles = function compareFiles(fileA, fileB) {
  return (fileA === null || fileA === void 0 ? void 0 : fileA.size) === (fileB === null || fileB === void 0 ? void 0 : fileB.size) && (fileA === null || fileA === void 0 ? void 0 : fileA.type) === (fileB === null || fileB === void 0 ? void 0 : fileB.type) && (fileA === null || fileA === void 0 ? void 0 : fileA.name) === (fileB === null || fileB === void 0 ? void 0 : fileB.name);
};

var setupRejectionErrors = function setupRejectionErrors(fileRejections, setError, t) {
  fileRejections.forEach(function (file) {
    file.errors.forEach(function (err) {
      if (err.code === ErrorCode.FileInvalidType) {
        return setError(t('file-upload-type-error'));
      }

      if (err.code === ErrorCode.FileTooLarge) {
        return setError(t('file-upload-size-error'));
      }

      if (err.code === ErrorCode.TooManyFiles) {
        return setError(t('file-upload-limit-error'));
      }

      return setError(t('file-upload-generic-error'));
    });
  });
};

export var useFileUpload = function useFileUpload(_ref) {
  var _ref$accept = _ref.accept,
      accept = _ref$accept === void 0 ? acceptedMimeTypes : _ref$accept,
      _ref$maxFiles = _ref.maxFiles,
      maxFiles = _ref$maxFiles === void 0 ? MAX_FILES : _ref$maxFiles,
      _ref$maxSize = _ref.maxSize,
      maxSize = _ref$maxSize === void 0 ? MAX_FILE_SIZE : _ref$maxSize,
      _ref$maxTotalSize = _ref.maxTotalSize,
      maxTotalSize = _ref$maxTotalSize === void 0 ? MAX_TOTAL_FILE_SIZE : _ref$maxTotalSize,
      _ref$initialFiles = _ref.initialFiles,
      initialFiles = _ref$initialFiles === void 0 ? [] : _ref$initialFiles,
      onDragOver = _ref.onDragOver,
      onDragLeave = _ref.onDragLeave,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(null),
      error = _useState[0],
      setError = _useState[1];

  var _useState2 = useState(initialFiles),
      images = _useState2[0],
      setImages = _useState2[1];

  var _useState3 = useState(null),
      sizeArr = _useState3[0],
      setSizeArr = _useState3[1]; // Keep track of files already added and update maxFiles


  var internalMaxFiles = maxFiles - images.length;
  var onDrop = useCallback(function (acceptedFiles, fileRejections) {
    var uniqueAcceptedFiles = acceptedFiles.filter(function (file) {
      return images.every(function (image) {
        return !compareFiles(file, image.file);
      });
    });
    setError(null); // react-dropzone handles maxFiles = 0 as unlimited files so we need to handle 0 maxFiles ourselves

    if (internalMaxFiles === 0 || uniqueAcceptedFiles.length > internalMaxFiles) {
      return setError(t('file-upload-limit-error'));
    }

    setupRejectionErrors(fileRejections, setError, t);
    var currentSize = sizeArr ? sumArr(sizeArr) : 0;
    uniqueAcceptedFiles.forEach(function (file) {
      var fileMimeType = getFileTypeFromMime(file.type);
      var url = isImageMimeType(fileMimeType) ? URL.createObjectURL(file) : undefined;
      currentSize += file.size;

      if (currentSize > maxTotalSize) {
        return setError(t('file-upload-total-size-error'));
      }

      setImages(function (currentImages) {
        return [].concat(_toConsumableArray(currentImages), [_objectSpread({
          fileUuid: v4(),
          fileMimeType: fileMimeType,
          file: file,
          proxy: false
        }, url && {
          thumbnail: {
            url: url
          }
        })]);
      });
      setSizeArr(function (prev) {
        if (!prev) {
          return [file.size];
        }

        return [].concat(_toConsumableArray(prev), [file.size]);
      });
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [images, maxFiles]);

  var _useDropzone = useDropzone({
    onDrop: onDrop,
    accept: accept,
    maxSize: maxSize,
    maxFiles: internalMaxFiles,
    onDragOver: onDragOver,
    onDragLeave: onDragLeave,
    disabled: disabled
  }),
      getRootProps = _useDropzone.getRootProps,
      getInputProps = _useDropzone.getInputProps,
      inputRef = _useDropzone.inputRef,
      acceptedFiles = _useDropzone.acceptedFiles;
  /**
   * Removes a single file from the list of images
   * @param id File uuid
   * @param index File index position
   */


  var removeFile = function removeFile(id, index) {
    // Update image state
    setImages(images.filter(function (_ref2) {
      var fileUuid = _ref2.fileUuid;
      return fileUuid !== id;
    }));

    if (sizeArr) {
      // Immutable array - remove current file size from array
      var newSizeArr = [].concat(_toConsumableArray(sizeArr.slice(0, index)), _toConsumableArray(sizeArr.slice(index + 1)));
      setSizeArr(newSizeArr);
    } // Remove item from acceptedFiles


    acceptedFiles.splice(index, 1);
  };
  /**
   * Removes all files from the uploader and resets hook state
   */


  var removeAll = function removeAll() {
    acceptedFiles.length = 0; // Make sure to clear input ref

    if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
      inputRef.current.value = '';
    }

    setSizeArr(null);
    setImages([]);
  };

  var resetError = function resetError() {
    setError(null);
  };

  return {
    getRootProps: getRootProps,
    getInputProps: getInputProps,
    images: images,
    error: error,
    removeFile: removeFile,
    removeAll: removeAll,
    resetError: resetError
  };
};